/**
 * <archive-gallery>
 * Tap a tile → the image grows from its tile to the centre (FLIP), the rest of
 * the grid fades. Prev / next, close, ← → Esc, swipe. No price, no product page.
 */
if (!customElements.get('archive-gallery')) {
  customElements.define(
    'archive-gallery',
    class ArchiveGallery extends HTMLElement {
      connectedCallback() {
        this.showTitle = this.dataset.showTitle === 'true';
        this.showDate = this.dataset.showDate === 'true';
        this.items = [];
        this.index = -1;
        this.addEventListener('click', (e) => {
          const item = e.target.closest('[data-gallery-item]');
          if (!item) return;
          // Linked tiles (drops) navigate to the product; plain tiles (archive) zoom.
          if (item.tagName === 'A') return;
          e.preventDefault();
          this.open(item);
        });
        this.onKey = this.onKey.bind(this);
        this.hoverEnabled =
          this.dataset.hover !== 'false' &&
          window.matchMedia('(hover: hover)').matches &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
          !document.documentElement.classList.contains('no-motion');
        if (this.hoverEnabled) {
          this.addEventListener('pointerover', (e) => {
            const item = e.target.closest('[data-gallery-item]');
            if (item && item !== this.hovered) this.hover(item);
          });
          this.addEventListener('pointerleave', () => this.hover(null));
        }
      }

      /* Grow the hovered tile; push the others away with a falloff over ~2 tile widths. */
      hover(item) {
        if (this.classList.contains('is-open')) return;
        this.collect();
        this.hovered = item;
        const GROW = 1.35;
        const PUSH = 18; // px at the closest neighbour
        if (!item) {
          this.items.forEach((el) => {
            el.classList.remove('is-hovered');
            el.style.setProperty('--grow', 1);
            el.style.setProperty('--push-x', '0px');
            el.style.setProperty('--push-y', '0px');
          });
          return;
        }
        const a = item.getBoundingClientRect();
        const ax = a.left + a.width / 2;
        const ay = a.top + a.height / 2;
        const reach = a.width * 2.2;
        this.items.forEach((el) => {
          if (el === item) {
            el.classList.add('is-hovered');
            el.style.setProperty('--grow', GROW);
            el.style.setProperty('--push-x', '0px');
            el.style.setProperty('--push-y', '0px');
            return;
          }
          el.classList.remove('is-hovered');
          el.style.setProperty('--grow', 1);
          const b = el.getBoundingClientRect();
          const dx = b.left + b.width / 2 - ax;
          const dy = b.top + b.height / 2 - ay;
          const dist = Math.hypot(dx, dy) || 1;
          const force = Math.max(0, 1 - dist / reach);
          const amount = PUSH * force * force;
          el.style.setProperty('--push-x', `${((dx / dist) * amount).toFixed(1)}px`);
          el.style.setProperty('--push-y', `${((dy / dist) * amount).toFixed(1)}px`);
        });
      }

      collect() {
        this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      }

      buildOverlay() {
        if (this.overlay) return;
        const o = document.createElement('div');
        o.className = 'archive-zoom';
        o.setAttribute('role', 'dialog');
        o.setAttribute('aria-modal', 'true');
        o.innerHTML = `
          <button type="button" class="archive-zoom__close" data-close aria-label="${window.accessibilityStrings?.close || 'Close'}">&times;</button>
          <button type="button" class="archive-zoom__nav archive-zoom__nav--prev" data-prev aria-label="Previous">&larr;</button>
          <figure class="archive-zoom__figure">
            <span class="archive-zoom__media">
              <img class="archive-zoom__image archive-zoom__image--thumb" alt="" decoding="async">
              <img class="archive-zoom__image archive-zoom__image--full" alt="" decoding="async" aria-hidden="true">
            </span>
            <figcaption class="archive-zoom__meta">
              <span class="archive-zoom__title" hidden></span>
              <span class="archive-zoom__date" hidden></span>
            </figcaption>
          </figure>
          <button type="button" class="archive-zoom__nav archive-zoom__nav--next" data-next aria-label="Next">&rarr;</button>
          <p class="archive-zoom__counter" aria-live="polite"></p>`;
        // Inherit the section's colour scheme so the overlay is black on dark pages
        const scheme = Array.from(this.closest('[class*="color-"]')?.classList || []).find((c) =>
          c.startsWith('color-')
        );
        if (scheme) o.classList.add(scheme);
        document.body.appendChild(o);
        this.overlay = o;
        this.figure = o.querySelector('.archive-zoom__figure');
        this.img = o.querySelector('.archive-zoom__image--thumb');
        this.full = o.querySelector('.archive-zoom__image--full');
        this.caption = o.querySelector('.archive-zoom__title');
        this.date = o.querySelector('.archive-zoom__date');
        this.media = o.querySelector('.archive-zoom__media');
        this.counter = o.querySelector('.archive-zoom__counter');
        o.querySelector('[data-close]').addEventListener('click', () => this.close());
        o.querySelector('[data-prev]').addEventListener('click', () => this.step(-1));
        o.querySelector('[data-next]').addEventListener('click', () => this.step(1));
        this.media.addEventListener('click', () => this.close());
        o.addEventListener('click', (e) => {
          if (e.target === o) this.close();
        });
        let startX = 0;
        o.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
        o.addEventListener(
          'touchend',
          (e) => {
            const dx = e.changedTouches[0].clientX - startX;
            if (Math.abs(dx) > 60) this.step(dx < 0 ? 1 : -1);
          },
          { passive: true }
        );
      }

      open(item) {
        this.hover(null);
        this.collect();
        this.buildOverlay();
        this.index = this.items.indexOf(item);
        this.trigger = item;
        this.classList.add('is-open');
        this.overlay.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onKey);
        this.show(true);
        this.overlay.querySelector('[data-close]').focus();
      }

      show(animateFromTile) {
        const item = this.items[this.index];
        if (!item) return;
        const idx = this.index;
        // Instant: reuse the tile's already-loaded image, then upgrade to the 2000px file.
        const thumb = item.querySelector('img');
        this.img.src = thumb?.currentSrc || thumb?.src || item.dataset.full;
        this.img.alt = item.dataset.title || '';
        // High-res layered on top; fades in once decoded, so nothing ever flashes.
        this.full.classList.remove('is-ready');
        this.full.src = item.dataset.full;
        const reveal = () => {
          if (this.index === idx && this.overlay.classList.contains('is-open')) this.full.classList.add('is-ready');
        };
        if (this.full.complete && this.full.naturalWidth) {
          this.full.decode?.().then(reveal).catch(reveal) ?? reveal();
        } else {
          this.full.onload = () => (this.full.decode ? this.full.decode().then(reveal).catch(reveal) : reveal());
        }

        this.caption.textContent = item.dataset.title || '';
        this.caption.hidden = !this.showTitle || !item.dataset.title;
        this.date.textContent = item.dataset.date || '';
        this.date.hidden = !this.showDate || !item.dataset.date;
        this.counter.textContent = `${this.index + 1} / ${this.items.length}`;
        this.overlay.querySelector('[data-prev]').disabled = this.index === 0;
        this.overlay.querySelector('[data-next]').disabled = this.index === this.items.length - 1;

        const fig = this.figure;
        fig.classList.remove('is-animating');
        if (!animateFromTile) {
          // prev/next: quick crossfade-scale
          fig.style.transform = 'scale(0.96)';
          fig.style.opacity = '0';
          fig.getBoundingClientRect();
          requestAnimationFrame(() => {
            fig.classList.add('is-animating');
            fig.style.transform = 'none';
            fig.style.opacity = '1';
          });
          return;
        }
        // FLIP: start exactly on the tile, then zoom up to the centre
        fig.style.transform = 'none';
        fig.style.opacity = '1';
        const from = item.getBoundingClientRect();
        const to = fig.getBoundingClientRect();
        if (!to.width || !to.height) return;
        const scale = Math.min(from.width / to.width, from.height / to.height);
        const dx = from.left + from.width / 2 - (to.left + to.width / 2);
        const dy = from.top + from.height / 2 - (to.top + to.height / 2);
        fig.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
        fig.getBoundingClientRect(); // commit the start state before animating
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fig.classList.add('is-animating');
            fig.style.transform = 'none';
          });
        });
      }

      step(dir) {
        const next = this.index + dir;
        if (next < 0 || next >= this.items.length) return;
        this.index = next;
        this.show(false);
      }

      close() {
        if (!this.overlay) return;
        this.overlay.classList.remove('is-open');
        this.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onKey);
        this.trigger?.focus();
      }

      onKey(e) {
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowRight') this.step(1);
        else if (e.key === 'ArrowLeft') this.step(-1);
      }
    }
  );
}
